// topoEditor/docResolve.js — batch status resolver for {id} doc-links.
//
// id → { exists, published, archive_id, domain }. One ajax batch per set of not-yet-known ids; results are cached
// per id (module-level, request-lifetime). Backend = the public read-only `action=resolveDocs` (includes/ajax.php).
// Consumers: the frontend info-text (grey out unpublished/gone links, decorate foreign-archive links) and later the
// editor (typo guard). Never poisons the cache on a network error — a later call retries.

const AJAX_URL = "/includes/ajax.php";
const cache = new Map();   // id(string) → { exists, published, archive_id, domain }
const NOT_FOUND = { exists: false, published: false, archive_id: null, domain: null };

// ajax.php is CSRF-guarded (enforceCsrf tiers). Send the same header jQuery's global $.ajaxSetup sends (basic.js):
// the token from <meta name="csrf-token">. Without it the endpoint returns { error: "No CSRF token." }.
function csrfHeaders() {
    const m = document.querySelector('meta[name="csrf-token"]');
    const tok = m && m.getAttribute("content");
    return tok ? { Csrftoken: tok } : {};
}

// Response keys are the PLAIN integer document_id (no leading zeros); a doc-link's {id} may carry leading zeros
// (e.g. {0737577}). Normalize for the response lookup so "0737577" matches the response key "737577".
function normId(id) { return String(parseInt(id, 10)); }

// ids: (string|number)[]  →  Promise<{ [id:string]: {exists, published, archive_id, domain} }>  (keyed by the ORIGINAL id)
export async function resolveDocs(ids) {
    const wanted = [...new Set((ids || []).map(String).filter((s) => /^\d+$/.test(s)))];
    const missing = wanted.filter((id) => !cache.has(id));
    if (missing.length) {
        let dids = null;
        try {
            const res = await fetch(AJAX_URL + "?action=resolveDocs&dids=" + missing.join(","), {
                credentials: "same-origin",
                headers: csrfHeaders(),
            });
            if (res.ok) {
                const json = await res.json();
                // Only a VALID response (success + dids) caches. An error response ({error:"…CSRF…"}, no dids) must
                // NOT poison the cache with NOT_FOUND for every id — leave dids null so a later call retries.
                if (json && json.success && json.dids && typeof json.dids === "object") { dids = json.dids; }
                else if (json && json.error) { console.warn("[docResolve] endpoint error (not cached):", json.error); }
            }
        } catch (e) { /* network error → leave dids null, don't cache */ }
        if (dids) {
            missing.forEach((id) => cache.set(id, dids[normId(id)] || NOT_FOUND));   // lookup by normalized key
        }
    }
    const out = {};
    wanted.forEach((id) => { out[id] = cache.get(id) || NOT_FOUND; });
    return out;
}
